import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { RadiologyLiveIcon } from '@/components/icons/ProductIcons';

export default function Waitlist() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [professionalRole, setProfessionalRole] = useState('');
  const [institution, setInstitution] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('waitlist-join', {
        body: {
          email: email.trim().toLowerCase(),
          full_name: fullName.trim(),
          professional_role: professionalRole,
          institution: institution.trim() || undefined,
          reason: reason.trim() || undefined,
        },
      });

      if (error) {
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
          description: 'Please try again later.',
        });
        setLoading(false);
        return;
      }

      setSubmitted(true);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Something went wrong',
        description: 'Please try again later.',
      });
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="relative z-10 w-full max-w-md">
          <div className="glass-strong rounded-2xl p-8 text-center">
            <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">You're on the list!</h1>
            <p className="text-muted-foreground mb-6">
              We'll reach out when your invite is ready. Keep an eye on your inbox.
            </p>
            <div className="space-y-3">
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Back to Home
                </Button>
              </Link>
              <p className="text-sm text-muted-foreground">
                Already have an invite?{' '}
                <Link to="/signup" className="text-primary hover:underline">
                  Sign up here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="relative z-10 w-full max-w-md">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>

        {/* Card */}
        <div className="glass-strong rounded-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 mb-4">
              <RadiologyLiveIcon size={48} />
            </div>
            <h1 className="text-2xl font-bold">Join the Waitlist</h1>
            <p className="text-muted-foreground mt-2">
              RadiologyLive.ai is currently in private beta. Request early access below.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Dr. Jane Smith"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="bg-muted/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="doctor@hospital.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-muted/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Professional Role *</Label>
              <Select value={professionalRole} onValueChange={setProfessionalRole} required>
                <SelectTrigger className="bg-muted/50">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="radiologist">Radiologist</SelectItem>
                  <SelectItem value="trainee">Trainee / Resident</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="institution">Institution (optional)</Label>
              <Input
                id="institution"
                type="text"
                placeholder="e.g. Mass General Hospital"
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                className="bg-muted/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Why are you interested? (optional)</Label>
              <Textarea
                id="reason"
                placeholder="Tell us briefly what you're looking for..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="bg-muted/50 resize-none"
                rows={3}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading || !fullName || !email || !professionalRole}
            >
              {loading ? 'Submitting...' : 'Join the Waitlist'}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an invite?{' '}
            <Link to="/signup" className="text-primary hover:underline">
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
